import React, { useEffect, useState } from 'react';
import { Modal, Form, Button, Row, Col } from 'react-bootstrap';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { FaArrowDown, FaArrowUp, FaBriefcase, FaUser } from 'react-icons/fa';

const ACCENT = '#60A5FA';

const FREQUENCY_CHOICES = [
  { value: 'daily', label: 'Daily' },
  { value: 'weekly', label: 'Weekly' },
  { value: 'monthly', label: 'Monthly' },
  { value: 'quarterly', label: 'Quarterly' },
  { value: 'yearly', label: 'Yearly' },
];

const emptyForm = () => ({
  name: '',
  description: '',
  subscription_type: 'outgoing',
  category: 'personal',
  amount: '',
  currency: 'EUR',
  frequency: 'monthly',
  start_date: new Date(),
  end_date: null,
  company: '',
});

const SubscriptionModal = ({ show, onHide, onSubmit, initialData }) => {
  const [form, setForm] = useState(emptyForm());

  useEffect(() => {
    if (!show) return;
    if (initialData) {
      setForm({
        name: initialData.name || '',
        description: initialData.description || '',
        subscription_type: initialData.subscription_type || 'outgoing',
        category: initialData.category || 'personal',
        amount: String(initialData.amount ?? ''),
        currency: initialData.currency || 'EUR',
        frequency: initialData.frequency || 'monthly',
        start_date: initialData.start_date ? new Date(initialData.start_date) : new Date(),
        end_date: initialData.end_date ? new Date(initialData.end_date) : null,
        company: initialData.company || '',
      });
    } else {
      setForm(emptyForm());
    }
  }, [show, initialData]);

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit({
      name: form.name.trim(),
      description: form.description.trim() || null,
      subscription_type: form.subscription_type,
      category: form.category,
      amount: Math.abs(parseFloat(form.amount) || 0),
      currency: form.currency,
      frequency: form.frequency,
      start_date: form.start_date,
      end_date: form.end_date,
      company: form.company.trim() || null,
    });
  };

  return (
    <Modal show={show} onHide={onHide} size="lg" centered contentClassName="bg-dark text-light">
      <Modal.Header closeButton closeVariant="white" style={{ borderBottom: '1px solid #333' }}>
        <Modal.Title>{initialData ? 'Edit subscription' : 'Add subscription'}</Modal.Title>
      </Modal.Header>
      <Form onSubmit={handleSubmit}>
        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label>Direction</Form.Label>
            <div className="d-flex gap-2">
              <Button
                type="button"
                variant={form.subscription_type === 'incoming' ? 'success' : 'outline-success'}
                className="flex-grow-1"
                onClick={() => setForm((p) => ({ ...p, subscription_type: 'incoming' }))}
              >
                <FaArrowDown className="me-2" /> I get money
              </Button>
              <Button
                type="button"
                variant={form.subscription_type === 'outgoing' ? 'danger' : 'outline-danger'}
                className="flex-grow-1"
                onClick={() => setForm((p) => ({ ...p, subscription_type: 'outgoing' }))}
              >
                <FaArrowUp className="me-2" /> I pay
              </Button>
            </div>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Category</Form.Label>
            <div className="d-flex gap-2">
              <Button
                type="button"
                variant={form.category === 'business' ? 'primary' : 'outline-primary'}
                className="flex-grow-1"
                onClick={() => setForm((p) => ({ ...p, category: 'business' }))}
              >
                <FaBriefcase className="me-2" /> Business
              </Button>
              <Button
                type="button"
                variant={form.category === 'personal' ? 'secondary' : 'outline-secondary'}
                className="flex-grow-1"
                onClick={() => setForm((p) => ({ ...p, category: 'personal' }))}
              >
                <FaUser className="me-2" /> Personal
              </Button>
            </div>
          </Form.Group>

          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Name</Form.Label>
                <Form.Control
                  required
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="e.g. Netflix, Office rent, Client retainer"
                  className="bg-dark text-light border-secondary"
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Company / provider (optional)</Form.Label>
                <Form.Control
                  value={form.company}
                  onChange={(e) => setForm({ ...form, company: e.target.value })}
                  className="bg-dark text-light border-secondary"
                />
              </Form.Group>
            </Col>
          </Row>

          <Form.Group className="mb-3">
            <Form.Label>Notes</Form.Label>
            <Form.Control
              as="textarea"
              rows={2}
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              className="bg-dark text-light border-secondary"
            />
          </Form.Group>

          <Row>
            <Col md={4}>
              <Form.Group className="mb-3">
                <Form.Label>Amount</Form.Label>
                <Form.Control
                  type="number"
                  step="0.01"
                  min="0"
                  required
                  value={form.amount}
                  onChange={(e) => setForm({ ...form, amount: e.target.value })}
                  className="bg-dark text-light border-secondary"
                />
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group className="mb-3">
                <Form.Label>Currency</Form.Label>
                <Form.Select
                  value={form.currency}
                  onChange={(e) => setForm({ ...form, currency: e.target.value })}
                  className="bg-dark text-light border-secondary"
                >
                  <option value="EUR">EUR</option>
                  <option value="USD">USD</option>
                  <option value="GBP">GBP</option>
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group className="mb-3">
                <Form.Label>Frequency</Form.Label>
                <Form.Select
                  value={form.frequency}
                  onChange={(e) => setForm({ ...form, frequency: e.target.value })}
                  className="bg-dark text-light border-secondary"
                >
                  {FREQUENCY_CHOICES.map((c) => (
                    <option key={c.value} value={c.value}>{c.label}</option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
          </Row>

          <Row>
            <Col md={6}>
              <Form.Group className="mb-1">
                <Form.Label style={{ display: 'block' }}>Start date</Form.Label>
                <DatePicker
                  selected={form.start_date}
                  onChange={(date) => setForm({ ...form, start_date: date })}
                  dateFormat="dd-MM-yyyy"
                  className="form-control bg-dark text-light border-secondary"
                  required
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-1">
                <Form.Label style={{ display: 'block' }}>End date (optional)</Form.Label>
                <DatePicker
                  selected={form.end_date}
                  onChange={(date) => setForm({ ...form, end_date: date })}
                  dateFormat="dd-MM-yyyy"
                  className="form-control bg-dark text-light border-secondary"
                  isClearable
                  placeholderText="Ongoing"
                />
              </Form.Group>
            </Col>
          </Row>
        </Modal.Body>
        <Modal.Footer style={{ borderTop: '1px solid #333' }}>
          <Button variant="secondary" onClick={onHide}>Cancel</Button>
          <Button type="submit" variant="primary" style={{ backgroundColor: ACCENT, borderColor: ACCENT }}>
            {initialData ? 'Save' : 'Add subscription'}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default SubscriptionModal;
